/** Every AI call the app has made: model, duration, outcome. */
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { useApi } from '../lib/api'
import { TrendChart, BarsChart } from '../components/charts'
import { Card, Tile, Badge, Empty, QueryState, fmtInt, fmtDateTime, downloadCsv } from '../components/ui'

export default function AiUsageReport() {
  const query = useApi('stats-ai', 'stats/ai')

  return (
    <PageHeader title="AI Usage" sub="Prompt runs, models used, failures">
      <QueryState query={query}>
        {(d) => {
          const total = d.byModel.reduce((a, r) => a + r.n, 0)
          const errors = d.byModel.reduce((a, r) => a + r.errors, 0)
          const avgMs = d.byModel.length
            ? Math.round(d.byModel.reduce((a, r) => a + r.avg_ms * r.n, 0) / (total || 1))
            : 0

          if (!total) {
            return (
              <Card>
                <Empty icon="ai" title="No AI runs yet">
                  Once someone proofreads an update or extracts prayer requests, every call shows
                  up here with the model and how long it took.
                </Empty>
              </Card>
            )
          }

          return (
            <div className="col">
              <div className="grid grid-4">
                <Tile label="Total runs" value={fmtInt(total)} />
                <Tile label="Failures" value={fmtInt(errors)} tone={errors ? 'danger' : undefined} note={`${Math.round((errors / total) * 100)}% of runs`} />
                <Tile label="Average latency" value={`${fmtInt(avgMs)} ms`} />
                <Tile label="Models in use" value={fmtInt(d.byModel.length)} />
              </div>

              <div className="grid grid-2">
                <Card title="Runs per Day" sub="Last 60 days with activity">
                  <TrendChart
                    data={d.byDay.slice().reverse()}
                    x="day"
                    series={[
                      { key: 'n', label: 'Runs' },
                      { key: 'errors', label: 'Failures' },
                    ]}
                    height={240}
                  />
                </Card>
                <Card title="Runs by Model">
                  <BarsChart
                    data={d.byModel}
                    x="model_key"
                    series={[{ key: 'n', label: 'Runs' }]}
                    horizontal
                    height={240}
                  />
                </Card>
              </div>

              <Card title="By Model">
                <div className="table-wrap flush">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Model</th>
                        <th>Provider</th>
                        <th className="right">Runs</th>
                        <th className="right">Failures</th>
                        <th className="right">Avg latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.byModel.map((r, i) => (
                        <tr key={i}>
                          <td className="mono small">{r.model_key}</td>
                          <td>{r.provider_key}</td>
                          <td className="num">{fmtInt(r.n)}</td>
                          {/*
                            The count leads to the failures it counted: the error log,
                            filtered to this model.

                            `status=all` because the log opens on unresolved rows and this
                            number was counted over every run, so the default would leave
                            someone looking at a shorter list than the one they clicked.
                            The model alone and no severity: a caption a vision model
                            declined is a failed run here and a *warning* there, and
                            severity=error would hide exactly those.

                            The two tallies are still counted from different tables —
                            ai_runs against error_log — so a model whose failures predate
                            the error log has a number here and fewer rows there.
                          */}
                          <td className="num">
                            {r.errors ? (
                              <Link
                                className="badge badge-danger"
                                to={`/settings/errors?model=${encodeURIComponent(r.model_key)}&status=all`}
                                title={`Show what went wrong with ${r.model_key} in the error log`}
                              >
                                {r.errors}
                              </Link>
                            ) : (
                              <span className="subtle">0</span>
                            )}
                          </td>
                          <td className="num">{fmtInt(r.avg_ms)} ms</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>

              <Card
                title="Recent Runs"
                sub="Most recent 100"
                actions={
                  <button
                    className="btn btn-sm"
                    onClick={() =>
                      downloadCsv('ai-runs.csv', d.recent, [
                        { key: 'created_at', label: 'When' },
                        { key: 'user_email', label: 'User' },
                        { key: 'prompt_name', label: 'Prompt' },
                        { key: 'model_key', label: 'Model' },
                        { key: 'field_key', label: 'Field' },
                        { key: 'status', label: 'Status' },
                        { key: 'ms', label: 'ms' },
                        { key: 'input_chars', label: 'Input chars' },
                        { key: 'output_chars', label: 'Output chars' },
                      ])
                    }
                  >
                    ↓ CSV
                  </button>
                }
              >
                <div className="table-wrap flush">
                  <table className="data compact">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>User</th>
                        <th>Prompt</th>
                        <th>Field</th>
                        <th>Model</th>
                        <th className="right">ms</th>
                        <th className="right">In → out</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.recent.map((r) => (
                        <tr key={r.id}>
                          <td className="nowrap small">{fmtDateTime(r.created_at)}</td>
                          <td className="small">{r.user_email}</td>
                          <td className="small">{r.prompt_name ?? <span className="subtle">ad-hoc</span>}</td>
                          <td className="tiny mono">{r.field_key}</td>
                          <td className="tiny mono">{r.model_key}</td>
                          <td className="num">{fmtInt(r.ms)}</td>
                          <td className="num tiny">
                            {fmtInt(r.input_chars)} → {fmtInt(r.output_chars)}
                          </td>
                          <td>
                            {r.status === 'ok' ? (
                              <Badge tone="ok">ok</Badge>
                            ) : (
                              <Badge tone="danger" title={r.error}>
                                error
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
